<template>
	<div>
		<TnTextField
			class="margin-bottom-xl"
			label="Fornavn"
			placeholder="Skriv inn ditt fornavn..."
			v-model="customer.firstName"
			@blur="validateInput('firstName')"
			maxlength="50"
		>
			<template
				v-if="firstNameError.status"
				#error
			>
				{{ firstNameError.message }}
			</template>
		</TnTextField>
		<TnTextField
			class="margin-bottom-xl"
			label="Etternavn"
			placeholder="Skriv inn ditt etternavn..."
			v-model="customer.lastName"
			@blur="validateInput('lastName')"
			maxlength="50"
		>
			<template
				v-if="lastNameError.status"
				#error
			>
				{{ lastNameError.message }}
			</template>
		</TnTextField>
		<TnTextField
			v-model="customer.email"
			@blur="validateInput('email')"
			class="margin-bottom-xl"
			name="email"
			label="E-postadresse"
			type="email"
			placeholder="Eks. “ola.nordmann@mail.no”"
		>
			<template
				v-if="emailError.status"
				#error
			>
				{{ emailError.message }}
			</template>
		</TnTextField>
		<TnTextField
			v-model="customer.phoneNumber"
			@blur="validateInput('phone')"
			class="margin-bottom-xl"
			name="phone"
			label="Mobilnummer"
			type="text"
			placeholder="8 siffer"
			inputmode="numeric"
		>
			<template #icon-left> +47 </template>
			<template
				v-if="phoneError.status"
				#error
			>
				{{ phoneError.message }}
			</template>
		</TnTextField>
		<RichText
			:text="content.obsText"
			class="body margin-bottom-xl"
		/>

		<TnNotification
			v-if="orderError.status"
			type="error"
			title="Det har skjedd en feil"
		>
			<p>
				{{ orderError.message }}
			</p>
		</TnNotification>

		<TnButton
			class="margin-bottom-2xl"
			@click="reserve"
			:disabled="!canSubmit || submitting"
			:loading="submitting"
			block
			>{{ content.confirmReservationButtonText }}</TnButton
		>
	</div>
</template>

<script>
import clickAndCollectIntegration from "~/integrations/click-and-collect-integration";
import customerIntegration from "~/integrations/customers";
import telenorid from "~/telenorid/telenorid";
import { trackOrderClickAndCollect } from "~/components/ecommerce/checkout/ecommerceOrderTracking.js";

export default defineNuxtComponent({
	name: "ECommerceClickAndCollectCustomerForm",

	props: {
		store: {
			type: Object,
			required: true,
		},
		hardwareProductId: {
			type: String,
			required: true,
		},
		content: {
			type: Object,
		},
	},

	data: () => ({
		customer: {
			firstName: "",
			lastName: "",
			email: "",
			phoneNumber: "",
			message: "",
		},
		phoneError: {
			status: false,
			message: "",
		},
		emailError: {
			status: false,
			message: "",
		},
		orderError: {
			status: false,
			message: "",
		},
		firstNameError: {
			status: false,
			message: "",
		},
		lastNameError: {
			status: false,
			message: "",
		},
		messageError: {
			status: false,
			message: "",
		},
		submitting: false,
	}),

	async mounted() {
		await this.getUserDetails();
	},

	computed: {
		canSubmit() {
			return !!(
				!this.emailError.status &&
				this.customer.email &&
				!this.phoneError.status &&
				this.customer.phoneNumber &&
				this.customer.firstName &&
				this.customer.lastName
			);
		},
	},

	methods: {
		async getUserDetails() {
			const user = await telenorid.getOrLoginUser();

			if (user) {
				try {
					const customer = await customerIntegration.getCustomerData();
					const contactDetails = await customerIntegration.getContactDetails();

					this.customer.firstName = customer?.firstName || "";
					this.customer.lastName = customer?.lastName || "";
					this.customer.email = contactDetails?.emailAddress || "";
				} catch (e) {
					console.error(e);
				}
			}
		},
		async reserve() {
			this.orderError.status = false;
			this.submitting = true;
			this.emailError.status = false;
			this.phoneError.status = false;

			this.validateInput();

			const data = {
				firstname: this.customer.firstName,
				lastname: this.customer.lastName,
				email: this.customer.email,
				phone: this.customer.phoneNumber,
				dealerId: this.store.storeId,
				hardwareProductId: this.hardwareProductId,
				message: this.customer.message,
			};

			if (this.canSubmit) {
				try {
					const res = await clickAndCollectIntegration.reserveProduct(data);
					this.$track(trackOrderClickAndCollect(data));
					this.$emit("order-successful", res);
				} catch (err) {
					console.error(err);
					this.orderError.message = err.description;
					this.orderError.status = true;
					this.submitting = false;
				}
			}
		},
		validateInput(fieldName) {
			if (fieldName === "email" || !fieldName) {
				if (!this.customer.email) {
					this.emailError.status = true;
					this.emailError.message = "Dette feltet er påkrevd.";
				} else if (!this.validateEmail(this.customer.email)) {
					this.emailError.status = true;
					this.emailError.message = "Du må oppgi en gyldig e-postadresse.";
				} else {
					this.emailError.status = false;
					this.emailError.message = "";
				}
			}

			if (fieldName === "phone" || !fieldName) {
				if (!this.customer.phoneNumber) {
					this.phoneError.status = true;
					this.phoneError.message = "Dette feltet er påkrevd.";
				} else if (!this.validatePhoneNumber(this.customer.phoneNumber)) {
					this.phoneError.status = true;
					this.phoneError.message = "Du må oppgi et gyldig telefonnummer.";
				} else {
					this.phoneError.status = false;
					this.phoneError.message = "";
				}
			}

			if (fieldName === "firstName" || !fieldName) {
				if (!this.customer.firstName) {
					this.firstNameError.status = true;
					this.firstNameError.message = "Dette feltet er påkrevd";
				} else if (!this.validateText(this.customer.firstName, true)) {
					this.firstNameError.status = true;
					this.firstNameError.message = "Fornavnet inneholder ugyldige tegn.";
				} else {
					this.firstNameError.status = false;
					this.firstNameError.message = "";
				}
			}

			if (fieldName === "lastName" || !fieldName) {
				if (!this.customer.firstName) {
					this.lastNameError.status = true;
					this.lastNameError.message = "Dette feltet er påkrevd";
				} else if (!this.validateText(this.customer.lastName, true)) {
					this.lastNameError.status = true;
					this.lastNameError.message = "Etternavnet inneholder ugyldige tegn.";
				} else {
					this.lastNameError.status = false;
					this.lastNameError.message = "";
				}
			}

			if (fieldName === "message" || !fieldName) {
				if (!this.validateText(this.customer.message)) {
					this.messageError.status = true;
					this.messageError.message = "Meldingen inneholder ugyldige tegn.";
				} else {
					this.messageError.status = false;
					this.messageError.message = "";
				}
			}
		},
		validateEmail(email) {
			const pattern = "^\\s*([\\w\\._+-]+)@([\\w\\._+-]+)\\.([A-Za-z\\._+-]{2,})\\s*$";

			return RegExp(pattern).test(email);
		},
		validatePhoneNumber(phoneNumber) {
			return RegExp("[0-9]{8}").test(phoneNumber);
		},
		validateText(text, name = false) {
			if (!name && !text) return true;

			if ((name && text.length > 50) || (!name && text.length > 200)) return false;
			if (name) {
				return RegExp("^[^#%&*:<>!?/{|}]+$").test(text);
			} else {
				return RegExp("^[^#%&*:<>/{|}]+$").test(text);
			}
		},
	},
});
</script>

<style lang="scss" scoped></style>
